import {
  Button,
  Center,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';

import {
  CheckOutline,
  CrossOutline,
  DropdownInput,
  DropdownOption,
  Individual,
  IndividualRole,
  useToast,
  CloseCircleOutline,
} from '@aslanpay/portal-common-lib';

import { Role } from 'app/constants/individual';

import useAuthContext from 'app/hooks/useAuthContext';
import useCreateAdmin from 'app/hooks/useCreateAdmin';
import useFetchCompanies from 'app/hooks/useFetchCompanies';

import { createAdminFormSchema } from 'app/utils/schemas';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import { yupResolver } from '@hookform/resolvers/yup';

import { CreateAdminFormFields, CreateAdminModalProps } from './types';

function CreateAdminModal({ isOpen, onClose }: CreateAdminModalProps) {
  const { user } = useAuthContext();

  const toast = useToast();

  const queryClient = useQueryClient();

  const { view } = useNavigationStore.getState();

  const { company: currentCompany } = useManagingCompanyStore.getState();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    resetField,
    setFocus,
    formState: { errors, isValid },
  } = useForm<CreateAdminFormFields>({
    mode: 'onBlur',
    defaultValues: {
      role: view === 'global' && user?.role === 'ASLAN_ADMIN' ? 'ASLAN_ADMIN' : 'COMPANY_ADMIN',
      email: '',
      invite: true,
      companyId: currentCompany?.id,
      canTopUpCompanyPoints: false,
    },
    resolver: yupResolver(createAdminFormSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const resetAndFocusField = (field: keyof CreateAdminFormFields) => {
    resetField(field, { defaultValue: '' });
    setFocus(field);
  };

  const { mutate: createAdmin, isLoading: isCreatingAdmin } = useCreateAdmin({
    onSuccess: (admin: Individual) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'New admin created',
        description: `${admin.email} was successfully created!`,
      });

      queryClient.invalidateQueries({ queryKey: ['fetchAdminsPage'] });

      handleClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the new admin was not created. Please try again.',
      });
    },
  });

  const handleCreateAdmin = (data: CreateAdminFormFields) => {
    createAdmin({
      role: data.role,
      email: data.email,
      invite: data.invite,
      companyId: data.companyId,
      canTopUpCompanyPoints: data.canTopUpCompanyPoints,
      salaried: false, // Set salaried to false for admin roles
    });
  };

  const roleOptions: DropdownOption[] = (
    view === 'global' && user?.role === 'ASLAN_ADMIN'
      ? (['ASLAN_ADMIN', 'COMPANY_ADMIN'] as IndividualRole[])
      : []
  ).map((role) => ({
    value: role as string,
    text: Role[role],
  }));

  const { data: companies, isLoading: isLoadingCompanies } = useFetchCompanies({
    page: 0,
    size: 999999,
    sortBy: 'name',
    sort: 'ASC',
  });

  let companyOptions: DropdownOption[] = [];

  if (view === 'global') {
    companyOptions =
      companies?.list.map((company) => ({
        value: company.id,
        text: company.name,
      })) || [];
  }

  const watchRole = watch('role');
  const watchEmail = watch('email');

  const handleChangeRole = (event: string, onChange: (value: string) => void) => {
    resetField('companyId');
    onChange(event);
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Create a new admin</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create new admin"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          {isLoadingCompanies ? (
            <Center width="full" height="full" flexGrow={1}>
              <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
            </Center>
          ) : (
            <form id="create-admin-form" onSubmit={handleSubmit(handleCreateAdmin)} noValidate>
              <Stack spacing="20px">
                {watchRole === 'COMPANY_ADMIN' && view === 'company' && (
                  <Text textStyle="smallSemiBold">
                    Company selected:{' '}
                    <Text as="span" color="brand.purple" textStyle="smallMedium">
                      {currentCompany?.name}
                    </Text>{' '}
                  </Text>
                )}
                {roleOptions.length > 1 && (
                  <FormControl size="lg" isInvalid={!!errors?.role?.message} isRequired>
                    <FormLabel>Admin role</FormLabel>
                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          onChange={(event) => handleChangeRole(event, field.onChange)}
                        >
                          <Stack spacing="14px">
                            {roleOptions.map((role) => (
                              <Radio key={role.value} value={role.value as string}>
                                {role.text}
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>
                      )}
                    />

                    <FormErrorMessage>{errors?.role?.message}</FormErrorMessage>
                  </FormControl>
                )}
                <FormControl isInvalid={!!errors?.email?.message} isRequired>
                  <FormLabel>New admin</FormLabel>
                  <InputGroup>
                    <Input type="email" placeholder="Enter email address" {...register('email')} />
                    {watchEmail && (
                      <InputRightElement
                        h="full"
                        cursor="pointer"
                        onClick={() => resetAndFocusField('email')}
                      >
                        <CrossOutline color="brand.darkGray" boxSize="24px" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
                </FormControl>

                {watchRole === 'COMPANY_ADMIN' && view === 'global' && (
                  <FormControl isInvalid={!!errors?.companyId?.message} isRequired>
                    <FormLabel>Select a company</FormLabel>
                    <Controller
                      control={control}
                      name="companyId"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Select the company that the admin will manage"
                          defaultValue={companyOptions.find((company) => company.value === value)}
                          options={companyOptions}
                          onChange={onChange}
                          onBlur={onBlur}
                          isSearchable
                          searchPlaceholder="Search company..."
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.companyId?.message}</FormErrorMessage>
                  </FormControl>
                )}

                {watchRole === 'COMPANY_ADMIN' && (
                  <Checkbox size="md" {...register('canTopUpCompanyPoints')}>
                    Can top up company points
                  </Checkbox>
                )}

                <Checkbox size="md" {...register('invite')}>
                  Send invitation email
                </Checkbox>
              </Stack>
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form="create-admin-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isCreatingAdmin}
            isDisabled={!isValid}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateAdminModal;
